import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

export interface AlertShowData {
  type: string;
  text: string;
  imageUrl: string;
  soundUrl: string;
  durationMs: number;
  volume: number;
  textSize?: number;
  textAlign?: 'left' | 'center' | 'right';
  layout?: string;
  payload?: Record<string, unknown>;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(-10px) scale(0.98); }
`;

const Stage = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const layoutStyles: Record<string, ReturnType<typeof css>> = {
  text_over_image: css`
    position: relative;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  text_top_image_bottom: css`
    flex-direction: column;
    align-items: center;
  `,
  text_bottom_image_top: css`
    flex-direction: column-reverse;
    align-items: center;
  `,
  text_left_image_right: css`
    flex-direction: row;
    align-items: center;
    text-align: left;
  `,
  text_right_image_left: css`
    flex-direction: row-reverse;
    align-items: center;
    text-align: right;
  `,
};

const AlertBox = styled.div<{ $visible: boolean; $layout: string }>`
  display: flex;
  gap: 0.75rem;
  max-width: 92%;
  text-align: center;
  animation: ${({ $visible }) => ($visible ? fadeIn : fadeOut)} 0.45s ease forwards;
  ${({ $layout }) => layoutStyles[$layout] || layoutStyles.text_bottom_image_top}
`;

const AlertImg = styled.img`
  max-width: min(320px, 55vw);
  max-height: 240px;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(255, 20, 147, 0.35));
`;

const AlertText = styled.div<{ $size: number; $align: string; $overlay?: boolean }>`
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: ${({ $size }) => `${$size}px`};
  font-weight: 700;
  color: #fff;
  text-align: ${({ $align }) => $align};
  white-space: pre-line;
  text-shadow:
    0 0 12px rgba(255, 20, 147, 0.8),
    0 2px 8px rgba(0, 0, 0, 0.9),
    2px 2px 0 #9d4edd;
  padding: 0.5rem 1rem;
  line-height: 1.3;
  ${({ $overlay, $align }) => $overlay && css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: ${$align === 'left' ? 'flex-start' : $align === 'right' ? 'flex-end' : 'center'};
    padding: 1rem;
  `}
`;

const OverlayWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  filterType?: string | null;
}

const AlertPlayer: React.FC<Props> = ({ filterType }) => {
  const [current, setCurrent] = useState<AlertShowData | null>(null);
  const [visible, setVisible] = useState(false);
  const queueRef = useRef<AlertShowData[]>([]);
  const playingRef = useRef(false);

  const playNext = useCallback(() => {
    if (playingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) {
      setCurrent(null);
      setVisible(false);
      return;
    }

    playingRef.current = true;
    setCurrent(next);
    setVisible(true);

    if (next.soundUrl) {
      const audio = new Audio(next.soundUrl);
      audio.volume = Math.max(0, Math.min(1, (next.volume ?? 80) / 100));
      audio.play().catch(() => {});
    }

    const duration = next.durationMs || 5000;
    window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(() => {
        playingRef.current = false;
        playNext();
      }, 450);
    }, duration);
  }, []);

  const enqueue = useCallback((alert: AlertShowData) => {
    if (filterType && alert.type !== filterType) return;
    queueRef.current.push(alert);
    playNext();
  }, [filterType, playNext]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<AlertShowData>).detail;
      if (detail) enqueue(detail);
    };
    window.addEventListener('eugenial-alert', handler);
    return () => window.removeEventListener('eugenial-alert', handler);
  }, [enqueue]);

  if (!current) return null;

  const layout = current.layout || 'text_bottom_image_top';
  const textSize = current.textSize || 24;
  const textAlign = current.textAlign || 'center';
  const textEl = current.text ? (
    <AlertText $size={textSize} $align={textAlign} $overlay={layout === 'text_over_image'}>
      {current.text}
    </AlertText>
  ) : null;
  const imgEl = current.imageUrl ? <AlertImg src={current.imageUrl} alt="" /> : null;

  return (
    <Stage>
      <AlertBox $visible={visible} $layout={layout}>
        {layout === 'text_over_image' ? (
          <OverlayWrap>
            {imgEl}
            {textEl}
          </OverlayWrap>
        ) : (
          <>
            {textEl}
            {imgEl}
          </>
        )}
      </AlertBox>
    </Stage>
  );
};

export default AlertPlayer;
